/**
 * Interactive REPL session — the core `spacecode` experience.
 *
 * Connects to a cloud agent via WebSocket and provides a readline-based
 * prompt loop with persistent history, slash command autocomplete,
 * tool call rendering, and init animation.
 *
 * Usage:
 *   spacecode owner/repo   → startInteractiveSession({ repo: 'owner/repo' })
 *   spacecode              → (via hub) startInteractiveSession({})
 */

import * as readline from 'node:readline';
import * as os from 'node:os';
import * as path from 'node:path';
import chalk from 'chalk';
import { AgentClient } from './client.js';
import { FileSync } from './file-sync.js';
import { renderEvent, resetTurnState, trackTokens } from './renderer.js';
import { PersistentHistory } from './history.js';
import { completeSlashCommand, parseSlashCommand } from './slash-commands.js';
import { showInitAnimation, showResumeAnimation } from './init.js';
import {
  getTtyUrl,
  getAuthToken,
  createSession,
  listSessions,
  buildWsUrl,
  findGitRoot,
} from '../commands/agent.js';

// ── Exported helpers (tested independently) ──────────────────────────────────

/** Join backslash-continued lines into a single line. */
export function joinContinuationLines(input: string): string {
  return input.replace(/\s*\\\n\s*/g, ' ');
}

/** Build the rich prompt string. */
export function buildPrompt(sessionName?: string): string {
  const name = sessionName ? chalk.dim(` ${sessionName}`) : '';
  return `${chalk.bold('spacecode')}${name} ${chalk.dim('>')} `;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface InteractiveSessionOptions {
  /** GitHub repo (owner/repo) to work on. */
  repo?: string;
  /** Existing session ID to attach to instead of creating a new one. */
  sessionId?: string;
  /** Human-readable session name for easy resume. */
  name?: string;
}

// ── Main REPL ────────────────────────────────────────────────────────────────

/**
 * Start an interactive REPL session with a cloud agent.
 *
 * Creates (or attaches to) an agent session, connects via WebSocket,
 * sets up file sync if in a git repo, and enters a readline prompt loop.
 */
export async function startInteractiveSession(
  options: InteractiveSessionOptions,
): Promise<void> {
  const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);

  // Create or resume session
  let sessionId: string;
  let sessionName = options.name;
  const isResume = !!options.sessionId;
  let resumeSummary: string | null = null;

  if (options.sessionId) {
    sessionId = options.sessionId;
    const result = await createSession(ttyUrl, token, options.repo, sessionId);
    resumeSummary = result.summary ?? null;
  } else {
    const result = await createSession(ttyUrl, token, options.repo, undefined, options.name);
    sessionId = result.sessionId;
  }

  const wsUrl = buildWsUrl(ttyUrl, sessionId, token);

  // Detect local git repo for file sync
  const gitRoot = findGitRoot();
  let fileSync: FileSync | null = null;

  // Track whether a prompt is currently running
  let promptRunning = false;

  // ── Persistent history ──────────────────────────────────────────────────
  const historyPath = path.join(os.homedir(), '.spacecode', 'history');
  const history = new PersistentHistory(historyPath);
  const historyLines = history.load();

  // ── Readline interface ──────────────────────────────────────────────────
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stderr,
    prompt: buildPrompt(sessionName),
    terminal: true,
    history: historyLines,
    completer: (line: string) => {
      if (line.startsWith('/')) {
        const matches = completeSlashCommand(line);
        return [matches, line];
      }
      return [[], line];
    },
  });

  // ── Agent client ────────────────────────────────────────────────────────
  const agentClient = new AgentClient({
    wsUrl,
    onEvent: (event) => {
      // Forward to file sync
      fileSync?.handleServerEvent(event);
    },
    onReady: () => {
      promptRunning = false;
      setImmediate(() => {
        process.stderr.write('\n');
        rl.prompt();
      });
    },
    onDone: () => {
      // In REPL mode, onReady handles re-prompting.
    },
    onError: (message) => {
      promptRunning = false;
      process.stderr.write(chalk.red(`\nAgent error: ${message}\n`));
      setImmediate(() => rl.prompt());
    },
  });

  // ── Ctrl+C handling ──────────────────────────────────────────────────────
  let sigintCount = 0;
  let sigintTimer: ReturnType<typeof setTimeout> | null = null;

  const resetSigint = () => {
    sigintCount = 0;
    if (sigintTimer) {
      clearTimeout(sigintTimer);
      sigintTimer = null;
    }
  };

  rl.on('SIGINT', () => {
    if (promptRunning) {
      sigintCount++;
      if (sigintCount === 1) {
        process.stderr.write(chalk.dim('\n⏹ Cancelling...\n'));
        agentClient.cancel();
        sigintTimer = setTimeout(resetSigint, 2000);
      } else {
        cleanup();
        process.exit(130);
      }
    } else {
      sigintCount++;
      if (sigintCount >= 2) {
        cleanup();
        process.exit(130);
      }
      process.stderr.write(chalk.dim('\n(Press Ctrl+C again to exit, or type /quit)\n'));
      sigintTimer = setTimeout(resetSigint, 2000);
      rl.prompt();
    }
  });

  // ── Line handler ────────────────────────────────────────────────────────
  rl.on('line', async (line) => {
    let input = line.trim();
    resetSigint();

    // Empty input → re-prompt
    if (!input) {
      rl.prompt();
      return;
    }

    // Multi-line paste: join backslash-continued lines
    input = joinContinuationLines(input);

    // Slash commands
    if (input.startsWith('/')) {
      const [cmd, args] = parseSlashCommand(input);

      switch (cmd) {
        case '/quit':
        case '/exit':
          cleanup();
          process.exit(0);
          return;

        case '/cancel':
          if (promptRunning) {
            process.stderr.write(chalk.dim('Cancelling...\n'));
            agentClient.cancel();
          } else {
            process.stderr.write(chalk.dim('Nothing to cancel.\n'));
          }
          rl.prompt();
          return;

        case '/clear':
          process.stderr.write('\x1B[2J\x1B[3J\x1B[H');
          rl.prompt();
          return;

        case '/name':
          if (args) {
            sessionName = args;
            rl.setPrompt(buildPrompt(sessionName));
            process.stderr.write(chalk.dim(`  Session named "${sessionName}"\n`));
          } else {
            process.stderr.write(chalk.dim('  Usage: /name <session-name>\n'));
          }
          rl.prompt();
          return;

        case '/status':
          process.stderr.write(chalk.dim(`  Session: ${sessionId}\n`));
          if (sessionName) process.stderr.write(chalk.dim(`  Name: ${sessionName}\n`));
          if (options.repo) process.stderr.write(chalk.dim(`  Repo: ${options.repo}\n`));
          process.stderr.write(chalk.dim(`  Running: ${promptRunning}\n`));
          rl.prompt();
          return;

        case '/sessions': {
          try {
            const sessions = await listSessions(ttyUrl, token, 10);
            if (sessions.length === 0) {
              process.stderr.write(chalk.dim('  No recent sessions.\n'));
            } else {
              process.stderr.write(chalk.dim('\n  Recent sessions:\n\n'));
              for (const s of sessions) {
                const name = s.name ? chalk.bold(s.name) : chalk.dim(s.id.slice(0, 8));
                const status = s.status === 'active' ? chalk.green('●') : chalk.dim('○');
                const age = formatAge(s.updated_at);
                process.stderr.write(`  ${status} ${name} ${chalk.dim(age)}\n`);
              }
              process.stderr.write('\n');
            }
          } catch {
            process.stderr.write(chalk.dim('  Failed to fetch sessions.\n'));
          }
          rl.prompt();
          return;
        }

        case '/history':
          try {
            const turns = await fetchHistory(ttyUrl, token, sessionId);
            if (turns.length === 0) {
              process.stderr.write(chalk.dim('No conversation history.\n'));
            } else {
              process.stderr.write(chalk.dim(`\n  Conversation (${turns.length} turns):\n\n`));
              for (const t of turns) {
                const role = t.role === 'user' ? chalk.cyan('You') : chalk.green('Agent');
                let text = '';
                try {
                  const parsed = JSON.parse(t.content);
                  if (typeof parsed === 'string') {
                    text = parsed;
                  } else if (Array.isArray(parsed)) {
                    text = parsed
                      .filter((b: any) => b.type === 'text')
                      .map((b: any) => b.text)
                      .join('\n');
                    const toolCount = parsed.filter((b: any) => b.type === 'tool_use').length;
                    if (toolCount > 0) text += chalk.dim(` [${toolCount} tool calls]`);
                  }
                } catch {
                  text = String(t.content);
                }
                if (text.length > 200) text = text.slice(0, 197) + '...';
                process.stderr.write(`  ${role}: ${text}\n`);
              }
              process.stderr.write('\n');
            }
          } catch {
            process.stderr.write(chalk.dim('Failed to fetch history.\n'));
          }
          rl.prompt();
          return;

        case '/compact':
          process.stderr.write(chalk.dim('  Compacting context...\n'));
          // Send a compact request to the agent
          agentClient.send({ type: 'prompt', text: '/compact' });
          promptRunning = true;
          return;

        case '/new':
          process.stderr.write(chalk.dim('  Starting fresh session...\n'));
          cleanup();
          await startInteractiveSession({ repo: options.repo });
          return;

        default:
          process.stderr.write(chalk.dim(`Unknown command: ${cmd}\n`));
          process.stderr.write(
            chalk.dim('Commands: /quit, /history, /clear, /status, /compact, /new, /sessions, /name\n'),
          );
          rl.prompt();
          return;
      }
    }

    // Send prompt to agent
    promptRunning = true;
    resetTurnState();
    history.append(input);
    agentClient.prompt(input);
  });

  // ── Close handler (EOF / Ctrl+D) ──────────────────────────────────────
  rl.on('close', () => {
    cleanup();
    process.exit(0);
  });

  // ── Cleanup helper ────────────────────────────────────────────────────
  function cleanup(): void {
    fileSync?.stop();
    agentClient.close();
    rl.close();
  }

  // ── Connect and start ─────────────────────────────────────────────────
  try {
    await agentClient.connect();
  } catch (error) {
    process.stderr.write(
      chalk.red(
        `Failed to connect: ${error instanceof Error ? error.message : String(error)}\n`,
      ),
    );
    cleanup();
    process.exit(1);
  }

  // Set up file sync after connection
  let fileCount = 0;
  if (gitRoot) {
    fileSync = new FileSync({
      repoRoot: gitRoot,
      send: (msg) => agentClient.send(msg),
    });
    fileCount = fileSync.syncInitial();
    fileSync.start();
  }

  // ── Init animation ────────────────────────────────────────────────────
  const ttyHost = new URL(wsUrl.replace('wss://', 'https://').replace('ws://', 'http://')).host;
  const version = '2.14.35'; // from package.json

  if (isResume) {
    // Fetch history count for resume banner
    let turnCount = 0;
    try {
      const turns = await fetchHistory(ttyUrl, token, sessionId);
      turnCount = turns.length;
    } catch {
      // Ignore — show 0 turns
    }
    await showResumeAnimation({
      sessionName: sessionName ?? sessionId.slice(0, 8),
      turnCount,
    });
    // EPIC-2 TICKET-2.2: show "where we left off" ahead of raw history, when
    // a prior extraction pass produced one. Sessions with none (new/short
    // sessions) fall back to the resume banner above unchanged.
    if (resumeSummary) {
      process.stderr.write(chalk.dim('Previously in this session:') + '\n');
      process.stderr.write(chalk.dim(resumeSummary) + '\n\n');
    }
  } else {
    // Get username from auth
    let username = 'user';
    try {
      const authPath = path.join(os.homedir(), '.spacecode', 'auth.json');
      const authData = JSON.parse(await import('fs').then((fs) => fs.readFileSync(authPath, 'utf-8')));
      username = authData.user?.login ?? authData.user?.name ?? 'user';
    } catch {
      // Ignore — use default
    }

    await showInitAnimation({
      version,
      username,
      sessionName: sessionName ?? sessionId.slice(0, 8),
      ttyHost,
      fileCount,
      repo: options.repo,
      branch: await getCurrentBranch(gitRoot),
    });
  }

  rl.prompt();
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Fetch conversation history for a session from the server.
 */
async function fetchHistory(
  ttyUrl: string,
  token: string,
  sessionId: string,
): Promise<Array<{ role: string; content: string }>> {
  const res = await fetch(`${ttyUrl}/api/agent/session/${sessionId}/history`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { messages: Array<{ role: string; content: string }> };
  return data.messages ?? [];
}

/**
 * Get the current git branch name, or undefined.
 */
async function getCurrentBranch(gitRoot: string | null): Promise<string | undefined> {
  if (!gitRoot) return undefined;
  try {
    const { execFileSync } = await import('child_process');
    return execFileSync('git', ['rev-parse', '--abbrev-ref', 'HEAD'], {
      cwd: gitRoot,
      encoding: 'utf-8',
    }).trim();
  } catch {
    return undefined;
  }
}

/**
 * Format a timestamp as relative time.
 */
function formatAge(timestamp: number): string {
  const ms = Date.now() - timestamp;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
